import React, { useState } from 'react';
import { ArrowLeft, Mail, RefreshCw, Lock, Phone } from 'lucide-react';
import { goToLogin, goHome } from '../utils/navigation';
import { apiService } from '../services/api';

type ForgotPasswordStep = 'request' | 'verify' | 'reset' | 'success';

const ForgotPassword: React.FC = () => {
  // Form data
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  
  // UI state
  const [step, setStep] = useState<ForgotPasswordStep>('request');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contactMethod, setContactMethod] = useState<'email' | 'phone'>('email');
  
  // OTP resend state
  const [timeLeft, setTimeLeft] = useState(120); // 2 minutes
  const [isResending, setIsResending] = useState(false);
  


  // Timer countdown
  React.useEffect(() => {
    if (timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [timeLeft]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Step 1: Request OTP
  const handleRequestOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const contactInfo = contactMethod === 'email' ? email : phone;
    
    if (!contactInfo.trim()) {
      setError(`Please enter your ${contactMethod}`);
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      await apiService.forgotPassword(
        contactMethod === 'email' ? email : undefined,
        contactMethod === 'phone' ? phone : undefined
      );
      
      setTimeLeft(120); // Reset timer
      setOtp(['', '', '', '', '', '']); // Reset OTP
      setStep('verify');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Verify OTP
  const handleVerifyOTP = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const otpString = otp.join('');
    if (otpString.length !== 6) {
      setError('Please enter the complete OTP');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.verifyResetOTP(
        contactMethod === 'phone' ? phone : email,
        otpString
      );
      
      if (response.success) {
        setStep('reset');
      } else {
        setError('Invalid OTP. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to verify OTP');
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP input changes
  const handleOtpChange = (index: number, value: string) => {
    if (value.length > 1) return; // Prevent multiple characters
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`forgot-otp-${index + 1}`);
      nextInput?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      const prevInput = document.getElementById(`forgot-otp-${index - 1}`);
      prevInput?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    const newOtp = [...otp];
    
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      if (/^\d$/.test(pastedData[i])) {
        newOtp[i] = pastedData[i];
      }
    }
    
    setOtp(newOtp);
  };

  // Handle resend OTP
  const handleResendOTP = async () => {
    setIsResending(true);
    try {
      await apiService.forgotPassword(
        contactMethod === 'email' ? email : undefined,
        contactMethod === 'phone' ? phone : undefined
      );
      setTimeLeft(120);
      setOtp(['', '', '', '', '', '']);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to resend OTP');
    } finally {
      setIsResending(false);
    }
  };

  // Step 3: Reset Password
  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!newPassword.trim()) {
      setError('Please enter a new password');
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await apiService.resetPassword(
        contactMethod === 'phone' ? phone : email,
        newPassword
      );
      
      if (response.success) {
        setStep('success');
      } else {
        setError('Failed to reset password. Please try again.');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    if (step === 'verify') {
      setStep('request');
    } else if (step === 'reset') {
      setStep('verify');
    } else {
      goHome();
    }
  };

  const getStepTitle = () => {
    switch (step) {
      case 'request': return 'Forgot Password';
      case 'verify': return 'Verify OTP';
      case 'reset': return 'Reset Password';
      case 'success': return 'Password Reset Successful';
      default: return 'Forgot Password';
    }
  };

  const getStepDescription = () => {
    switch (step) {
      case 'request': return 'Enter your email address or phone number to reset your password';
      case 'verify': return `Enter the OTP sent to your ${contactMethod}`;
      case 'reset': return 'Enter your new password';
      case 'success': return 'Your password has been reset successfully';
      default: return '';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-dpn-pink/10 via-white to-dpn-olive/10 flex items-center justify-center px-4 py-16">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8">
        {/* Header */}
        <div className="text-center mb-8">
          {step !== 'success' && (
            <button
              onClick={goBack}
              className="absolute top-8 left-8 text-dpn-olive hover:text-dpn-pink transition-colors duration-200"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
          )}
          <div className="flex justify-center mb-4">
            <img 
              src="/logo.png" 
              alt="Durga Puja Network" 
              className="h-20 w-20 object-contain"
              onError={(e) => (e.currentTarget.style.display = 'none')}
            />
          </div>
          <h1 className="text-3xl font-lucy-rose font-bold text-gray-800 mb-2">
            {getStepTitle()}
          </h1>
          <p className="text-gray-600">
            {getStepDescription()}
          </p>
        </div>

        {/* Progress Indicator */}
        {step !== 'success' && (
          <div className="mb-8">
            <div className="flex justify-between items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === 'request' ? 'bg-dpn-pink text-white' : 'bg-green-500 text-white'
              }`}>
                1
              </div>
              <div className={`flex-1 h-2 mx-2 rounded ${
                ['verify', 'reset'].includes(step) ? 'bg-green-500' : 'bg-gray-200'
              }`} />
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === 'verify' ? 'bg-dpn-pink text-white' : 
                step === 'reset' ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                2
              </div>
              <div className={`flex-1 h-2 mx-2 rounded ${
                step === 'reset' ? 'bg-green-500' : 'bg-gray-200'
              }`} />
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                step === 'reset' ? 'bg-dpn-pink text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                3
              </div>
            </div>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* Step Content */}
        {step === 'request' && (
          <form onSubmit={handleRequestOTP} className="space-y-6">
            {/* Contact Method Toggle */}
            <div className="flex space-x-4 mb-4">
              <button
                type="button"
                onClick={() => setContactMethod('email')}
                className={`flex-1 py-2 px-4 rounded-lg border ${
                  contactMethod === 'email' 
                    ? 'border-dpn-pink bg-dpn-pink/10 text-dpn-pink' 
                    : 'border-gray-300 text-gray-500'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Mail className="h-5 w-5" />
                  <span>Email</span>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setContactMethod('phone')}
                className={`flex-1 py-2 px-4 rounded-lg border ${
                  contactMethod === 'phone' 
                    ? 'border-dpn-pink bg-dpn-pink/10 text-dpn-pink' 
                    : 'border-gray-300 text-gray-500'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <Phone className="h-5 w-5" />
                  <span>Phone</span>
                </div>
              </button>
            </div>

            <div>
              <label htmlFor="contactInfo" className="block text-sm font-lucy-rose font-medium text-gray-700 mb-2">
                {contactMethod === 'email' ? 'Email Address' : 'Phone Number'}
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  {contactMethod === 'email' ? <Mail className="h-5 w-5" /> : <Phone className="h-5 w-5" />}
                </div>
                <input
                  type={contactMethod === 'email' ? 'email' : 'tel'}
                  id="contactInfo"
                  value={contactMethod === 'email' ? email : phone}
                  onChange={(e) => contactMethod === 'email' ? setEmail(e.target.value) : setPhone(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent transition-all duration-200"
                  placeholder={contactMethod === 'email' ? 'Enter your email address' : 'Enter your phone number'}
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-dpn-pink hover:bg-dpn-raspberry disabled:bg-gray-400 text-white font-lucy-rose font-medium py-3 px-6 rounded-lg transition-all duration-200 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <RefreshCw className="h-5 w-5 animate-spin" />
                  <span>Sending OTP...</span>
                </div>
              ) : (
                'Send OTP'
              )}
            </button>
          </form>
        )}

        {step === 'verify' && (
          <form onSubmit={handleVerifyOTP} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-4 text-center font-lucy-rose">
                Enter verification code
              </label>
              <div className="flex justify-center space-x-3">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    id={`forgot-otp-${index}`}
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOtpChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    className="w-12 h-12 text-center text-2xl font-bold border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent outline-none"
                  />
                ))}
              </div>
            </div>

            {/* Timer and Resend */}
            <div className="text-center">
              {timeLeft > 0 ? (
                <p className="font-lucy-rose text-sm text-gray-600">
                  Resend code in{' '}
                  <span className="font-medium text-dpn-pink">
                    {formatTime(timeLeft)}
                  </span>
                </p>
              ) : (
                <button
                  type="button"
                  onClick={handleResendOTP}
                  disabled={isResending || loading}
                  className="font-lucy-rose text-sm text-dpn-pink hover:text-dpn-raspberry font-medium transition-colors disabled:opacity-50 flex items-center justify-center mx-auto"
                >
                  {(isResending || loading) ? (
                    <RefreshCw className="h-4 w-4 animate-spin mr-1" />
                  ) : null}
                  {(isResending || loading) ? 'Sending...' : 'Resend Code'}
                </button>
              )}
            </div>

            <button
              type="submit"
              disabled={loading || otp.join('').length !== 6}
              className="w-full bg-dpn-pink hover:bg-dpn-raspberry disabled:bg-gray-400 text-white font-lucy-rose font-medium py-3 px-6 rounded-lg transition-all duration-200 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <RefreshCw className="h-5 w-5 animate-spin" />
                  <span>Verifying...</span>
                </div>
              ) : (
                'Verify OTP'
              )}
            </button>
          </form>
        )}

        {step === 'reset' && (
          <form onSubmit={handleResetPassword} className="space-y-6">
            <div>
              <label htmlFor="newPassword" className="block text-sm font-lucy-rose font-medium text-gray-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <Lock className="h-5 w-5" />
                </div>
                <input
                  type="password"
                  id="newPassword"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent transition-all duration-200"
                  placeholder="Enter new password"
                />
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-lucy-rose font-medium text-gray-700 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
                  <Lock className="h-5 w-5" />
                </div>
                <input
                  type="password"
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-dpn-pink focus:border-transparent transition-all duration-200"
                  placeholder="Confirm new password"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-dpn-pink hover:bg-dpn-raspberry disabled:bg-gray-400 text-white font-lucy-rose font-medium py-3 px-6 rounded-lg transition-all duration-200 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <RefreshCw className="h-5 w-5 animate-spin" />
                  <span>Resetting...</span>
                </div>
              ) : (
                'Reset Password'
              )}
            </button>
          </form>
        )}

        {step === 'success' && (
          <div className="space-y-6 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
                <Lock className="h-8 w-8 text-green-600" />
              </div>
            </div>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="text-green-700 text-sm">
                Your password has been reset successfully. You can now log in with your new password.
              </p>
            </div>
            
            <button
              onClick={() => goToLogin()}
              className="w-full bg-dpn-pink hover:bg-dpn-raspberry text-white font-lucy-rose font-medium py-3 px-6 rounded-lg transition-all duration-200"
            >
              Go to Login
            </button>
          </div>
        )}

        {/* Back to Login */}
        {step === 'request' && (
          <div className="mt-8 text-center">
            <p className="text-gray-600">
              Remember your password?{' '}
              <button
                onClick={goToLogin}
                className="text-dpn-pink hover:text-dpn-raspberry font-lucy-rose font-medium transition-colors duration-200"
              >
                Sign In
              </button>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
